// components/project/SalItemProgressControl.jsx
// STEP 5B-2a · SAL QUANTITY CORE — controllo avanzamento voce SAL (Percentuale | Misura).
// Percentage mode: comportamento storico (slider, salvataggio batch via bulk-update).
// Quantity mode: quantità eseguita (semplice o per-riga misura), % DERIVATA read-only, salvataggio per-voce.
import React, { useState } from 'react';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Percent, Ruler, ChevronDown, ChevronRight, Save, SlidersHorizontal } from 'lucide-react';
import { salApi } from '../../lib/api';
import { toast } from 'sonner';
import { QuantityCorrectionDialog } from './QuantityCorrectionDialog';
import { fmtQty, fmtPct } from '../../lib/formatQty';
const fmtNum = (v) => fmtQty(v);

export const SalItemProgressControl = ({ item, currentPct, onPercentageChange, onQuantitySaved }) => {
  const locked = !!item.sal_mode_locked;
  const lockedMode = item.sal_accounting_mode || null;
  const currentQty = item.current_quantity ?? item.original_quantity ?? item.total_quantity ?? 0;
  const measurementLines = Array.isArray(item.sal_measurement_lines) ? item.sal_measurement_lines : [];
  const hasMeasures = measurementLines.length > 0;

  // Modalità effettiva: se lockata usa quella; altrimenti scelta locale (default percentage).
  const [choice, setChoice] = useState(lockedMode || 'percentage');
  const mode = locked ? lockedMode : choice;

  // Stato editing quantità
  const [executed, setExecuted] = useState(String(item.completed_quantity ?? 0));
  const [rowExec, setRowExec] = useState(() =>
    Object.fromEntries(measurementLines.map(r => [r.source_measurement_id, String(r.executed_quantity ?? 0)]))
  );
  const [expanded, setExpanded] = useState(false);
  const [savingQty, setSavingQty] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionRowId, setCorrectionRowId] = useState(null);

  const rowSum = hasMeasures
    ? measurementLines.reduce((s, r) => s + (parseFloat(String(rowExec[r.source_measurement_id]).replace(',', '.')) || 0), 0)
    : 0;
  const executedVal = hasMeasures ? rowSum : (parseFloat(String(executed).replace(',', '.')) || 0);
  const derivedPct = currentQty > 0 ? Math.min(100, (executedVal / currentQty) * 100) : 0;
  const residual = Math.max(0, currentQty - executedVal);

  const saveQuantity = async () => {
    setSavingQty(true);
    try {
      const payload = { sal_accounting_mode: 'quantity' };
      if (hasMeasures) {
        payload.measurement_execution = measurementLines.map(r => ({
          source_measurement_id: r.source_measurement_id,
          executed_quantity: parseFloat(String(rowExec[r.source_measurement_id]).replace(',', '.')) || 0,
        }));
      } else {
        payload.completed_quantity = executedVal;
      }
      await salApi.update(item.id, payload);
      toast.success('Avanzamento a misura salvato');
      onQuantitySaved && onQuantitySaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || 'Errore nel salvataggio a misura');
    } finally {
      setSavingQty(false);
    }
  };

  // Phase C · voci a MISURA (detailed): il padre è DERIVATO/READ-ONLY.
  // L'editing avviene esclusivamente nel blocco "Dettaglio misure". Nessun input padre, nessun
  // pulsante "Salva misura", nessun accordion "Righe misura" (fonte unica = sottorighe).
  if (hasMeasures) {
    const parentOriginal = item.original_quantity ?? item.total_quantity ?? 0;
    const parentCurrent = item.current_quantity ?? parentOriginal;
    const parentExecuted = item.completed_quantity ?? 0;
    const parentResidual = Math.max(0, parentCurrent - parentExecuted);
    const parentPct = item.completion_percentage != null
      ? item.completion_percentage
      : (parentCurrent > 0 ? Math.min(100, (parentExecuted / parentCurrent) * 100) : 0);
    return (
      <div className="space-y-2" data-testid={`sal-progress-control-${item.id}`}>
        <span className="inline-flex items-center gap-1 text-[11px] text-[#737373]" data-testid={`sal-mode-measure-${item.id}`}>
          <Ruler className="w-3 h-3" /> Misura (derivata dalle sottorighe)
        </span>
        <div className="grid grid-cols-5 gap-2 text-xs max-w-[720px]">
          <ReadCell label="Originale" value={`${fmtQty(parentOriginal)} ${item.unit || ''}`} testid={`sal-q-original-${item.id}`} />
          <ReadCell label="Corrente" value={`${fmtQty(parentCurrent)} ${item.unit || ''}`} testid={`sal-q-current-${item.id}`} />
          <ReadCell label="Eseguito" value={`${fmtQty(parentExecuted)} ${item.unit || ''}`} testid={`sal-q-executed-${item.id}`} highlight />
          <ReadCell label="Residuo" value={`${fmtQty(parentResidual)} ${item.unit || ''}`} testid={`sal-q-residual-${item.id}`} />
          <ReadCell label="Avanzamento" value={fmtPct(parentPct)} testid={`sal-q-progress-${item.id}`} highlight />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`sal-progress-control-${item.id}`}>
      {/* Toggle modalità (solo se NON lockata) */}
      {!locked ? (
        <div className="inline-flex rounded-md border border-[#e5e7eb] overflow-hidden" data-testid={`sal-mode-toggle-${item.id}`}>
          <button
            type="button"
            onClick={() => setChoice('percentage')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${mode === 'percentage' ? 'bg-[#FF7819] text-white' : 'bg-white text-[#737373] hover:bg-[#fafafa]'}`}
            data-testid={`sal-mode-percentage-${item.id}`}
          >
            <Percent className="w-3 h-3" /> Percentuale
          </button>
          <button
            type="button"
            onClick={() => setChoice('quantity')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${mode === 'quantity' ? 'bg-[#FF7819] text-white' : 'bg-white text-[#737373] hover:bg-[#fafafa]'}`}
            data-testid={`sal-mode-quantity-${item.id}`}
          >
            <Ruler className="w-3 h-3" /> Misura
          </button>
        </div>
      ) : (
        <span
          className="inline-flex items-center gap-1 text-[11px] text-[#737373]"
          data-testid={`sal-mode-locked-${item.id}`}
        >
          {lockedMode === 'quantity' ? <Ruler className="w-3 h-3" /> : <Percent className="w-3 h-3" />}
          {lockedMode === 'quantity' ? 'Misura' : 'Percentuale'}
        </span>
      )}

      {/* PERCENTAGE */}
      {mode === 'percentage' && (
        <div className="flex items-center gap-3 max-w-[560px]">
          <Slider
            value={[currentPct]}
            onValueChange={(values) => onPercentageChange(item.id, 'completion_percentage', values[0])}
            max={100}
            step={5}
            className="flex-1"
            data-testid={`sal-pct-slider-${item.id}`}
          />
          <Input
            type="number"
            value={currentPct}
            onChange={(e) => onPercentageChange(item.id, 'completion_percentage', parseFloat(e.target.value) || 0)}
            className="w-20 h-8 text-sm input-abitalo"
            min={0}
            max={100}
            data-testid={`sal-pct-input-${item.id}`}
          />
          <span className="text-xs text-[#737373]">%</span>
        </div>
      )}

      {/* QUANTITY */}
      {mode === 'quantity' && (
        <div className="space-y-2 max-w-[620px]" data-testid={`sal-quantity-editor-${item.id}`}>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="bg-white rounded border border-[#eee] p-1.5">
              <div className="text-[#a3a3a3]">Originario</div>
              <div className="font-semibold" data-testid={`sal-q-original-${item.id}`}>{fmtNum(item.original_quantity ?? item.total_quantity)} {item.unit}</div>
            </div>
            <div className="bg-white rounded border border-[#eee] p-1.5">
              <div className="text-[#a3a3a3]">Corrente</div>
              <div className="font-semibold" data-testid={`sal-q-current-${item.id}`}>{fmtNum(currentQty)} {item.unit}</div>
            </div>
            <div className="bg-white rounded border border-[#eee] p-1.5">
              <div className="text-[#a3a3a3]">Eseguito</div>
              <div className="font-semibold text-[#FF7819]" data-testid={`sal-q-executed-${item.id}`}>{fmtNum(executedVal)} {item.unit}</div>
            </div>
            <div className="bg-white rounded border border-[#eee] p-1.5">
              <div className="text-[#a3a3a3]">Residuo</div>
              <div className="font-semibold" data-testid={`sal-q-residual-${item.id}`}>{fmtNum(residual)} {item.unit}</div>
            </div>
          </div>

          {!hasMeasures ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#737373]">Quantità eseguita</span>
              <Input
                type="text"
                inputMode="decimal"
                value={executed}
                onChange={(e) => setExecuted(e.target.value)}
                className="w-24 h-8 text-sm input-abitalo"
                data-testid={`sal-q-executed-input-${item.id}`}
              />
              <span className="text-xs text-[#a3a3a3]">/ {fmtNum(currentQty)} {item.unit}</span>
            </div>
          ) : (
            <div className="border border-[#eee] rounded">
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center gap-1 px-2 py-1 text-xs text-[#737373] hover:bg-[#fafafa]"
                data-testid={`sal-measures-toggle-${item.id}`}
              >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Righe misura ({measurementLines.length})
              </button>
              {expanded && (
                <div className="px-2 pb-2 space-y-1">
                  {measurementLines.map(r => (
                    <div key={r.source_measurement_id} className="flex items-center gap-2 text-xs" data-testid={`sal-measure-row-${item.id}-${r.source_measurement_id}`}>
                      <span className="flex-1 truncate">{r.description || r.source_measurement_id}</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={rowExec[r.source_measurement_id]}
                        onChange={(e) => setRowExec(prev => ({ ...prev, [r.source_measurement_id]: e.target.value }))}
                        className="w-20 h-7 text-xs input-abitalo"
                        data-testid={`sal-measure-input-${item.id}-${r.source_measurement_id}`}
                      />
                      <span className="text-[#a3a3a3] w-16 text-right">/ {fmtNum(r.current_quantity)}</span>
                      {locked && lockedMode === 'quantity' && item.item_status !== 'non_eseguita' && (
                        <button
                          type="button"
                          onClick={() => { setCorrectionRowId(r.source_measurement_id); setCorrectionOpen(true); }}
                          className="text-[#FF7819] hover:text-[#e56a13] p-0.5"
                          title="Rettifica quantità di questa riga"
                          data-testid={`sal-measure-correction-${item.id}-${r.source_measurement_id}`}
                        >
                          <SlidersHorizontal className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-[#737373]" data-testid={`sal-q-derived-pct-${item.id}`}>
              Avanzamento derivato: <strong className="text-[#FF7819]">{fmtPct(derivedPct)}</strong>
            </span>
            <div className="flex items-center gap-2">
              {locked && lockedMode === 'quantity' && item.item_status !== 'non_eseguita' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 border-[#FF7819] text-[#FF7819] hover:bg-[#fff3ea]"
                  onClick={() => { setCorrectionRowId(null); setCorrectionOpen(true); }}
                  data-testid={`sal-rettifica-quantita-${item.id}`}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Rettifica quantità
                </Button>
              )}
              <Button
                size="sm"
                className="h-7 bg-[#FF7819] hover:bg-[#e56a13]"
                onClick={saveQuantity}
                disabled={savingQty}
                data-testid={`sal-q-save-${item.id}`}
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Salva misura
              </Button>
            </div>
          </div>

          <QuantityCorrectionDialog
            open={correctionOpen}
            onOpenChange={setCorrectionOpen}
            item={item}
            measurementRowId={correctionRowId}
            onDone={onQuantitySaved}
          />
        </div>
      )}
    </div>
  );
};

const ReadCell = ({ label, value, testid, highlight }) => (
  <div className="bg-white rounded border border-[#eee] p-1.5">
    <div className="text-[#a3a3a3]">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-[#FF7819]' : 'text-[#3C3C3C]'}`} data-testid={testid}>{value}</div>
  </div>
);

export default SalItemProgressControl;
